import { useRef, useEffect, useCallback } from 'react'

// Tuning constants
const STEP_DISTANCE = 40       // px per step during drag
const VELOCITY_THRESHOLD = 0.3 // min px/ms to trigger inertia
const FRICTION = 0.95          // per-frame multiplier
const MIN_VELOCITY = 0.05      // stop threshold px/ms
const WHEEL_DEBOUNCE = 100     // ms window for wheel accumulation

interface UseFlickInertiaOptions {
  onStep: (direction: 1 | -1) => void
  itemCount: number
  disabled?: boolean
}

export function useFlickInertia({ onStep, itemCount, disabled }: UseFlickInertiaOptions) {
  const containerRef = useRef<HTMLDivElement>(null)
  const isAnimatingRef = useRef(false)

  // Stable ref for onStep so the hook internals always call the latest version
  const onStepRef = useRef(onStep)
  onStepRef.current = onStep

  const disabledRef = useRef(disabled)
  disabledRef.current = disabled

  // Touch tracking
  const touchActiveRef = useRef(false)
  const touchStartYRef = useRef(0)
  const dragAccumRef = useRef(0)

  // Velocity ring buffer (last 3 points)
  const touchSamplesRef = useRef<{ y: number; t: number }[]>([])

  // Inertia animation
  const rafIdRef = useRef(0)
  const inertiaVelocityRef = useRef(0)
  const inertiaAccumRef = useRef(0)
  const lastFrameTimeRef = useRef(0)

  // Wheel accumulation
  const wheelAccumRef = useRef(0)
  const wheelTimerRef = useRef(0)

  const fireStep = useCallback((dir: 1 | -1) => {
    onStepRef.current(dir)
  }, [])

  const stopInertia = useCallback(() => {
    if (rafIdRef.current) {
      cancelAnimationFrame(rafIdRef.current)
      rafIdRef.current = 0
    }
    isAnimatingRef.current = false
    inertiaVelocityRef.current = 0
    inertiaAccumRef.current = 0
  }, [])

  const startInertia = useCallback((velocity: number) => {
    // velocity is px/ms, positive = downward swipe (scroll down / value increases)
    stopInertia()
    if (Math.abs(velocity) < VELOCITY_THRESHOLD) return

    isAnimatingRef.current = true
    inertiaVelocityRef.current = velocity
    inertiaAccumRef.current = 0
    lastFrameTimeRef.current = performance.now()

    const tick = (now: number) => {
      if (disabledRef.current) { stopInertia(); return }

      const dt = now - lastFrameTimeRef.current
      lastFrameTimeRef.current = now

      // Accumulate virtual px
      inertiaAccumRef.current += inertiaVelocityRef.current * dt
      // Fire steps
      while (Math.abs(inertiaAccumRef.current) >= STEP_DISTANCE) {
        if (inertiaAccumRef.current > 0) {
          fireStep(1)
          inertiaAccumRef.current -= STEP_DISTANCE
        } else {
          fireStep(-1)
          inertiaAccumRef.current += STEP_DISTANCE
        }
      }

      // Apply friction
      inertiaVelocityRef.current *= FRICTION

      if (Math.abs(inertiaVelocityRef.current) < MIN_VELOCITY) {
        stopInertia()
        return
      }

      rafIdRef.current = requestAnimationFrame(tick)
    }

    rafIdRef.current = requestAnimationFrame(tick)
  }, [fireStep, stopInertia])

  // Attach touch listeners (non-passive for preventDefault)
  useEffect(() => {
    const el = containerRef.current
    if (!el) return

    const onTouchStart = (e: TouchEvent) => {
      if (disabledRef.current) return
      // Cancel any running inertia
      stopInertia()

      touchActiveRef.current = true
      touchStartYRef.current = e.touches[0].clientY
      dragAccumRef.current = 0
      touchSamplesRef.current = [{ y: e.touches[0].clientY, t: performance.now() }]
    }

    const onTouchMove = (e: TouchEvent) => {
      if (!touchActiveRef.current) return
      e.preventDefault() // prevent native scroll

      const y = e.touches[0].clientY
      const now = performance.now()

      // Record sample (keep last 3)
      const samples = touchSamplesRef.current
      samples.push({ y, t: now })
      if (samples.length > 3) samples.shift()

      // Accumulate drag distance from start
      const totalDelta = y - touchStartYRef.current
      // How many steps have we already fired?
      const stepsFromAccum = Math.trunc(dragAccumRef.current / STEP_DISTANCE)
      const stepsFromTotal = Math.trunc(totalDelta / STEP_DISTANCE)
      const newSteps = stepsFromTotal - stepsFromAccum

      if (newSteps !== 0) {
        const dir = newSteps > 0 ? 1 : -1
        const count = Math.abs(newSteps)
        for (let i = 0; i < count; i++) {
          fireStep(dir as 1 | -1)
        }
        dragAccumRef.current = stepsFromTotal * STEP_DISTANCE
      }
    }

    const onTouchEnd = (_e: TouchEvent) => {
      if (!touchActiveRef.current) return
      touchActiveRef.current = false

      // Compute release velocity from the last two samples
      const samples = touchSamplesRef.current
      if (samples.length >= 2) {
        const last = samples[samples.length - 1]
        const prev = samples[samples.length - 2]
        const dt = last.t - prev.t
        if (dt > 0) {
          const velocity = (last.y - prev.y) / dt // px/ms, positive = downward
          startInertia(velocity)
        }
      }

      touchSamplesRef.current = []
    }

    el.addEventListener('touchstart', onTouchStart, { passive: true })
    el.addEventListener('touchmove', onTouchMove, { passive: false })
    el.addEventListener('touchend', onTouchEnd, { passive: true })

    return () => {
      el.removeEventListener('touchstart', onTouchStart)
      el.removeEventListener('touchmove', onTouchMove)
      el.removeEventListener('touchend', onTouchEnd)
    }
  }, [fireStep, startInertia, stopInertia])

  // Wheel handler (attached as React event)
  const handleWheel = useCallback((e: React.WheelEvent) => {
    e.preventDefault()
    if (disabledRef.current) return

    // Accumulate rapid wheel ticks
    wheelAccumRef.current += e.deltaY
    if (wheelTimerRef.current) clearTimeout(wheelTimerRef.current)

    wheelTimerRef.current = window.setTimeout(() => {
      const accum = wheelAccumRef.current
      wheelAccumRef.current = 0
      if (accum === 0) return

      const steps = Math.max(1, Math.round(Math.abs(accum) / 50))
      const dir = accum > 0 ? 1 : -1
      for (let i = 0; i < steps; i++) {
        fireStep(dir as 1 | -1)
      }
    }, WHEEL_DEBOUNCE)
  }, [fireStep])

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      stopInertia()
      if (wheelTimerRef.current) clearTimeout(wheelTimerRef.current)
    }
  }, [stopInertia])

  return { containerRef, handleWheel, isAnimating: isAnimatingRef.current }
}
